import { create } from 'zustand';
import type { Tier2ScriptRuntime } from '../../engine/tier2';

/**
 * Tier-2 counterpart to `src/studio/editor/scriptConsoleStore.ts` - same
 * shape and the same module-level-singleton posture (one live play session
 * at a time), holding a `Tier2ScriptRuntime` instead of tier 1's
 * `HollowScriptRuntime`. Kept as a separate store rather than widening the
 * editor one's `runtime` field to accept either type: the two are read by
 * disjoint UI (`DockConsole`/`PreviewConsoleOverlay` only ever mount inside
 * the editor chrome, `PlayScriptConsole` only ever mounts on
 * `/play/<slug>/live`), and this store only exists at all when
 * `TIER2_SCRIPTING_ENABLED` (`tier2ScriptingFeature.ts`) is on - see
 * `usePlayScriptRuntime.ts` for where it is populated.
 */
interface PlayScriptConsoleState {
  readonly runtime: Tier2ScriptRuntime | null;
  setRuntime: (runtime: Tier2ScriptRuntime | null) => void;
  /** The published game's declared capability grant
   * (`GameSetupDoc.scriptCapabilities`, task item 4's per-project grant),
   * mirrored here from `ScreenPlayRuntime`'s own fetch of the `gamesetup`
   * play doc. `undefined` means "not configured" - narrowed by the reader
   * (`narrowScriptCapabilities`, `editor-panels.tsx`) the same way tier 1's
   * console does, reusing that function rather than re-deriving it. */
  readonly allowedCapabilities: readonly string[] | undefined;
  setAllowedCapabilities: (names: readonly string[] | undefined) => void;
  /** Whether the play surface's console overlay is open, toggled the same
   * backtick shortcut PreviewRoute's own overlay uses - see
   * `PlayScriptConsole.tsx`. */
  readonly open: boolean;
  toggleOpen: () => void;
}

export const usePlayScriptConsoleStore = create<PlayScriptConsoleState>((set) => ({
  runtime: null,
  setRuntime: (runtime) => set({ runtime }),
  allowedCapabilities: undefined,
  setAllowedCapabilities: (allowedCapabilities) => set({ allowedCapabilities }),
  open: false,
  toggleOpen: () => set((state) => ({ open: !state.open })),
}));
